/**
 * Analytics Tracker for Product Pages
 * Tracks visitor actions and sends data to API endpoint
 */

class AnalyticsTracker {
  constructor(apiBaseUrl) {
    this.apiBaseUrl = apiBaseUrl;
    this.originalReferrerKey = 'analytics_original_referrer';
    this.sessionKey = 'analytics_session';
    this.apiKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJ1bmlxdWVfbmFtZSI6IkFtcFdpenotdklFRjt6Y254bjtjOTMxIiwicm9sZSI6IkV2ZW50VHJhY2tlciIsIm5iZiI6MTc3MDIxNTczOCwiZXhwIjoxODAxNzUxNzM4LCJpYXQiOjE3NzAyMTU3Mzh9.H86wkvI7fOGO1xl6Qob72C2nm1fG034P4vtw3OXRpKI';
  }

  /**
   * Initialize tracking on page load
   */
  init() {
    // Save original referrer if this is entry from external source
    this.saveOriginalReferrer();

    // Track page impression
    this.trackEvent(false);

    // Set up buy button click tracking
    this.setupBuyButtonTracking();
  }

  /**
   * Save the original referrer when user enters the site from external source
   * Updates referrer if user comes from a different external source
   * 
   * Behavior:
   * - External to Internal: Saves the external referrer, keeps it during internal navigation
   * - Internal to Internal: No change to stored referrer
   * - External to External: Updates to the new external referrer
   * - This allows tracking the most recent external source that brought the user to the site
   */
  saveOriginalReferrer() {
    const currentReferrer = document.referrer;
    const currentDomain = window.location.hostname;
    
    // Check if current referrer is from an external source
    if (currentReferrer && !this.isSameDomain(currentReferrer, currentDomain)) {
      // Coming from external source - update the stored referrer
      sessionStorage.setItem(this.originalReferrerKey, currentReferrer);
    } else if (!currentReferrer && !sessionStorage.getItem(this.originalReferrerKey)) {
      // Direct visit and no referrer stored yet - store empty string
      sessionStorage.setItem(this.originalReferrerKey, '');
    }
	
	if (!this.getSessionId()) {
		sessionStorage.setItem(this.sessionKey, this.generateSessionId(27))
	}
    // If navigating internally (same domain), keep existing stored referrer unchanged
  }

  generateSessionId(base) {
	return btoa(String.fromCharCode(...(Array.from(crypto.randomUUID()).map(f => f.charCodeAt(0) ^ base++))))
  }
  /**
   * Check if referrer URL is from the same domain as current site
   */
  isSameDomain(referrerUrl, currentDomain) {
    if (!referrerUrl) return false;
    
    try {
      const referrerHostname = new URL(referrerUrl).hostname;
      // Remove www. for comparison
      const cleanReferrer = referrerHostname.replace(/^www\./, '');
      const cleanCurrent = currentDomain.replace(/^www\./, '');
      
      return cleanReferrer === cleanCurrent;
    } catch (e) {
      return false;
    }
  }

  /**
   * Extract ampid parameter from URL
   */
  getSessionId() {
	  return sessionStorage.getItem(this.sessionKey)
  }
  
  getAmplifierId() {
    const urlParams = new URLSearchParams(window.location.search);
    return urlParams.get('ampid') || '';
  }

  /**
   * Detect if the user agent is a bot
   */
  isBot(userAgent) {
    const botPatterns = [
	  'OAI-SearchBot', 'GPTBot', 'PerplexityBot', 'ClaudeBot', 'Claude-SearchBot',
      'bot', 'crawl', 'spider', 'slurp', 'mediapartners',
      'googlebot', 'bingbot', 'yandexbot', 'baiduspider',
      'facebookexternalhit', 'twitterbot', 'linkedinbot',
      'whatsapp', 'telegrambot', 'slackbot', 'discordbot',
	    'amazonproductdiscovery'
    ];

    return botPatterns.some(pattern => userAgent.includes(pattern));
  }

  getBotType(userAgent) {
    const patterns = {
      "openai": ['oai-searchbot', 'gptbot'],
      "claude": ['anthropic-ai', 'claudebot', 'claude-searchbot', "claude.ai"],
      "gemini": ['gemini', 'bard', 'googlebot'],
      "perplexity": ['perplexitybot'],
      "amazon": ["amazonproductdiscovery"],
      'baidu': ['baiduspider']
    };
    
    for (let key in patterns) {
      if (patterns[key].find(f => userAgent.includes(f))) {
        return key;
      }
    }
    
    return 'other';
  }

  /**
   * Get the original referrer URL (from when user first entered the site)
   */
  getReferrer() {
    return sessionStorage.getItem(this.originalReferrerKey) || '';
  }

  /**
   * Identify the type of referrer
   */
  getReferrerType() {
    const referrer = this.getReferrer().toLowerCase();

    if (!referrer) {
      return 'direct';
    }

	const referrers = {
		"chatgpt": ["chatgpt.com", "chat.openai.com"],
		"claude": ["claude.ai"],
		"gemini": ['gemini.google.com', 'bard.google.com'],
		"google_search": ["google."],
		"bing_search": ["bing.com"],
		"yahoo_search": ["yahoo.com"],
		"facebook": ["facebook", "fb.com"],
		"twitter": ["x.com", "twitter", "t.co"],
		"linkedin": ["linkedin.com"],
		"instagram": ["instagram.com"],
		"pinterest": ["pinterest"],
		"youtube": ["youtube.com"],
		"medium": ["medium.com"],
		"tumblr": ["tumblr"],
		"tiktok": ["tiktok"],
		"snapchat": ["snapchat"],
		"quora": ["quora"],
		"reddit": ["reddit"]
	};
	
	for (let key in referrers) {
		if (referrers[key].find(f => referrer.includes(f))) {
			return key;
		}
	}
	
    return 'other';
  }

  /**
   * Track an event and send to API
   */
  async trackEvent(isAffiliateBuyClick = false, publisher_merchant_id) {
    const endpoint = `${this.apiBaseUrl}/api/statistics/trackevents/`;
    const userAgent = navigator.userAgent.toLowerCase();
    const isBot = this.isBot(userAgent)
    let botType = null;
    if (isBot) {
      botType = this.getBotType(userAgent)
    }

    if (!publisher_merchant_id) {
      const button = document.querySelector('[data-publisher-merchant]')
      if (button) {
        publisher_merchant_id = button.getAttribute('data-publisher-merchant');
      }
    }

    const data = {
      session_id: this.getSessionId(),
      publisher_merchant_id: publisher_merchant_id,
      page_url: window.location.href,
      amplifier_id: this.getAmplifierId(),
      is_bot: isBot,
	    bot_type: botType,
      user_agent: navigator.userAgent,
      referrer: this.getReferrer(),
      referrer_type: this.getReferrerType(),
      impressions: 1,
      affiliate_buy_clicks: isAffiliateBuyClick ? 1 : 0
    };

    try {
      const response = await fetch(endpoint, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': 'Bearer '+ this.apiKey
        },
        body: JSON.stringify(data)
      });

      //if (!response.ok) {
      //  console.error('Analytics tracking failed:', response.statusText);
      //}
    } catch (error) {
      //console.error('Analytics tracking error:', error);
    }
  }

  /**
   * Set up tracking for buy button clicks
   * Looks for buy buttons with common selectors
   */
  setupBuyButtonTracking() {
    // Common selectors for buy buttons
    const buyButtonSelectors = [
      //'.amazon-style-button-product',
      '[data-publisher-merchant]'
    ];

    // Find all buy buttons
    const buyButtons = [];
    buyButtonSelectors.forEach(selector => {
      const elements = document.querySelectorAll(selector);
      buyButtons.push(...elements);
    });

    // Add click event listeners
    buyButtons.forEach(button => {
      button.addEventListener('click', () => {
        this.trackEvent(true, button.getAttribute("data-publisher-merchant"));
      });
    });
  }

  /**
   * Manual method to track buy button clicks
   * Can be called directly from onclick handlers
   */
  trackBuyClick() {
    this.trackEvent(true);
  }
}

  //if (localStorage.getItem('analytics_test') == 'test') {
    document.addEventListener('DOMContentLoaded', () => {
        //const API_BASE_URL = 'https://2735-74-82-31-214.ngrok-free.app';
        const API_BASE_URL = 'https://ampwizz.com'; 
        
        window.analyticsTracker = new AnalyticsTracker(API_BASE_URL);

        window.analyticsTracker.init();
    });
  //}

